import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  GoogleAnalyticsService,
  StaticPageService,
  WordpressTestimonialsService,
} from '@app/services/_index';
import { HasAnalytics, StaticPageInterface, TestimonialInterface } from '@app/interfaces/_index';
import { RouteTypes, TestimonialViewTypes } from '@app/enums/_index';
import { StaticPage, Testimonial } from '@app/models/_index';
import { StaticPageAdapter, TestimonialAdapter } from '@app/adapters/_index';
import { Subscription } from 'rxjs';
import { environment } from '@environments/environment';
import { logGoogleAnalytics } from '@app/utilities/_index';

@Component({
  selector: 'app-beyond-cx-detail',
  templateUrl: './beyond-cx-detail.component.html',
  styleUrls: ['./beyond-cx-detail.component.scss'],
  standalone: false,
})
export class BeyondCxDetailComponent implements OnInit, OnDestroy, HasAnalytics {
  @ViewChild('iframe') iframe: ElementRef;
  @Input() name: string;
  sub: Subscription;
  isLoading: boolean;
  contentData: StaticPage;
  testimonials: Testimonial[] = [];
  roiCalculatorUrl: string;
  showTestimonials = environment.beyond.beyondcx.showTestimonials;
  showScheduleConsultation = environment.beyond.beyondcx.showScheduleConsultation;
  showROICalculatorBanner = environment.beyond.beyondcx.roiCalculator.showROICalculator;

  constructor(
    private staticPageService: StaticPageService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private staticPageAdapter: StaticPageAdapter,
    private testimonialAdapter: TestimonialAdapter,
    private wpTestimonialService: WordpressTestimonialsService
  ) {}

  /**
   * OnInit load page and testimonials
   */
  ngOnInit(): void {
    this.roiCalculatorUrl = environment.beyond.beyondcx.roiCalculator.url;
    // Sets the metadata and the title for beyondcx page
    this.getSingleTypePage(this.name);
    // Collects testimonials from WordPress
    this.getTestimonials();
  }

  /**
   * OnDestroy unsubcribe BeyondCX testimonials to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  /**
   * Send the query search label to Google Analytics
   * @param category - the source of the google analytics category string (ex. the page or component triggering the action)
   * @param action - the action to be (ex. click)
   * @param dataLabel - the label associated with the action
   */
  sendGoogleAnalytics(category: string, action: string, dataLabel: string): void {
    logGoogleAnalytics(category, action, dataLabel);
    this.googleAnalyticsService.sendEvent(category, action, dataLabel);
  }

  /**
   * Load BeyondCX Single Page content from WordPress
   * @param slug - slug string
   */
  getSingleTypePage(slug: string): void {
    this.isLoading = true;
    this.sub = this.staticPageService.getStaticPageDetail(slug).subscribe({
      next: (response: StaticPageInterface[]) => {
        if (response && response.length > 0) {
          response.filter((item: StaticPageInterface) => {
            // NOTE: The WordPress Slugified slug (based on page name) should match and
            // return one result
            if (item.slug === this.name) {
              this.contentData = this.staticPageAdapter.adapt(item);
            }
          });
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        console.log(error);
      },
      complete: () => {
        // Force is loading to stop and complete in case of any unhandled errors
        this.isLoading = false;
        console.log('Content completed');
      },
    });
  }

  /**
   * Collect testimonial from TestimonialService
   * Subscribe to BeyondCX testimonials
   */
  getTestimonials(): void {
    this.isLoading = true;
    this.sub = this.wpTestimonialService
      .getTestimonialsByView(TestimonialViewTypes.BEYOND_CX)
      .subscribe({
        next: (testimonials: TestimonialInterface[]) => {
          this.isLoading = false;
          if (testimonials) {
            testimonials.forEach((item: TestimonialInterface) => {
              if (item) {
                const testimonial = this.testimonialAdapter.filter(
                  item,
                  TestimonialViewTypes.BEYOND_CX
                );
                if (testimonial) {
                  this.testimonials.push(testimonial);
                }
              }
            });
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Content completed');
        },
      });
  }

  /**
   *  RouteTypes enums based on route types
   */
  public get routeTypes(): typeof RouteTypes {
    return RouteTypes;
  }
}
